// @ts-strict-ignore
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@saleor/macaw-ui";
import React from "react";

import { WalletLedgerEntry } from "../../types";

const useStyles = makeStyles(
  (theme: any) => ({
    tableContainer: {
      maxHeight: 480,
      overflowY: "auto",
    },
    credit: {
      color: theme.palette.success.main,
      fontWeight: 600,
    },
    debit: {
      color: theme.palette.error.main,
      fontWeight: 600,
    },
    chip: {
      textTransform: "capitalize",
      fontWeight: 500,
      fontSize: "0.75rem",
    },
    emptyState: {
      padding: theme.spacing(4),
      textAlign: "center",
      color: theme.palette.text.secondary,
    },
  }),
  { name: "WalletLedgerDialog" },
);

interface WalletLedgerDialogProps {
  open: boolean;
  onClose: () => void;
  customerName?: string;
  entries: WalletLedgerEntry[];
}

export const WalletLedgerDialog: React.FC<WalletLedgerDialogProps> = ({
  open,
  onClose,
  customerName,
  entries = [],
}) => {
  const classes = useStyles();

  const getSourceChip = (source: string) => {
    if (source === "real") {
      return (
        <Chip
          size="small"
          label="Real Money"
          variant="outlined"
          color="primary"
          className={classes.chip}
        />
      );
    }

    return (
      <Chip
        size="small"
        label="Promotional"
        variant="outlined"
        color="secondary"
        className={classes.chip}
      />
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        Wallet Transaction Ledger History
        {customerName && (
          <Typography variant="body2" color="textSecondary">
            Customer: {customerName}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers className={classes.tableContainer}>
        {entries.length === 0 ? (
          <Typography className={classes.emptyState}>
            No wallet transaction history recorded for this customer yet.
          </Typography>
        ) : (
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date & Time</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Source</TableCell>
                <TableCell align="right">Amount</TableCell>
                <TableCell align="right">Balance After</TableCell>
                <TableCell>Reference / Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map(entry => {
                const isCredit =
                  entry.entryType === "credit" || entry.entryType === "reservation_release";
                const isDebit =
                  entry.entryType === "debit" ||
                  entry.entryType === "reservation_lock" ||
                  entry.entryType === "expire_writeoff";

                return (
                  <TableRow key={entry.id}>
                    <TableCell>
                      {new Date(entry.createdAt).toLocaleString("en-IN", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ textTransform: "capitalize", fontWeight: 500 }}
                      >
                        {entry.entryType.replace("_", " ")}
                      </Typography>
                    </TableCell>
                    <TableCell>{getSourceChip(entry.balanceSource)}</TableCell>
                    <TableCell
                      align="right"
                      className={isCredit ? classes.credit : isDebit ? classes.debit : undefined}
                    >
                      {isCredit ? "+" : isDebit ? "-" : ""}₹{Number(entry.amount).toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="caption" display="block">
                        Real: ₹{Number(entry.balanceAfterReal).toFixed(2)}
                      </Typography>
                      <Typography variant="caption" display="block" color="textSecondary">
                        Promo: ₹{Number(entry.balanceAfterPromo).toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        style={{ maxWidth: 240, wordBreak: "break-word" }}
                      >
                        {entry.description ||
                          (entry.referenceType
                            ? `${entry.referenceType} (${entry.referenceId || ""})`
                            : "-")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button type="button" onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WalletLedgerDialog;
